import React from 'react';
import {
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  Build as BuildIcon,
  DirectionsCar as DirectionsCarIcon,
  Groups as GroupsIcon,
  ListAlt as ListAltIcon,
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import * as Role from 'constants/role';
import { isUsuarioUsoMuto } from 'utils';
import { canViewDigitadorReport } from '../../../../helpers/digitadorReportPermission';
import { canViewFinanceiroReport } from '../../../../helpers/financeiroReportPermission';
import { canViewInspecaoReport } from '../../../../helpers/inspecaoReportPermission';
import { canViewUsoMutuoReport } from '../../../../helpers/usoMutuoReportPermission';
import { renderFolhaPonto, renderPedidoVoo } from './renderMenuOption';
import { gestaoVeicularSectionShow, renderGestaoVeicular } from './renderGestaoVeicular';

const iconSx = { fontSize: 20, width: 20, height: 20 };

const mapFolhaPontoToV2 = (authData, moduleConfigurations, key = 'inspecao') =>
  renderFolhaPonto(authData, moduleConfigurations).map((o) => ({
    title: o.title,
    path: o.href,
    show: o.show,
    key,
  }));

const mapPedidoVooToV2 = (authData, moduleConfigurations) =>
  renderPedidoVoo(authData, moduleConfigurations).map((o) => ({
    title: o.title,
    path: o.href,
    show: o.show,
    key: o.key,
  }));

/**
 * Itens de primeiro nível do menu V2 (paridade com pages() do Sidebar legado).
 * Aninhamento depth 2+ (ex.: Inspeção > Relatórios) é só V2 — SidebarNav V1 suporta apenas 2 níveis (page.options plano).
 */
export const buildMainV2NavItems = ({ userRole, authData, moduleConfigurations }) => [
  {
    title: 'Meu Perfil',
    icon: <PersonIcon sx={iconSx} />,
    path: '/meu-perfil',
    show: true,
  },
  {
    title: 'Inspeção',
    icon: <AssignmentIcon sx={iconSx} />,
    show: true,
    key: 'inspecao',
    items: [
      {
        title: 'NSIRs',
        path: '/v2/nsirs',
        isActivePath: (pathname) => pathname.includes('/nsir') || pathname.includes('/v2/nsirs'),
        show: true,
        key: 'inspecao',
      },
      {
        title: 'Programação Diária',
        path: '/v2/programacao-diaria',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'inspecao',
      },
      ...mapPedidoVooToV2(authData, moduleConfigurations),
      {
        title: 'Relatórios',
        key: 'inspecao',
        show:
          canViewInspecaoReport({ authData, userRole })
          || canViewDigitadorReport({ authData, userRole })
          || canViewUsoMutuoReport({ authData, moduleConfigurations }),
        items: [
          {
            title: 'Inspeção',
            path: '/v2/relatorio-inspecao',
            show: canViewInspecaoReport({ authData, userRole }),
            key: 'inspecao',
          },
          {
            title: 'Digitadores',
            path: '/v2/relatorio-digitacao',
            show: canViewDigitadorReport({ authData, userRole }),
            key: 'inspecao',
          },
          {
            title: 'Pesagem Sucata',
            path: '/v2/relatorio-uso-mutuo',
            show: canViewUsoMutuoReport({ authData, moduleConfigurations }),
            key: 'inspecao',
          },
        ],
      },
      {
        title: 'Integrações',
        path: '/v2/integracoes',
        show: canViewDigitadorReport({ authData, userRole }),
        key: 'inspecao',
      },
    ],
  },
  {
    title: 'Recursos Humanos',
    icon: <GroupsIcon sx={iconSx} />,
    show: true,
    key: 'recursosHumanos',
    items: [
      {
        title: 'Convocações',
        path: '/v2/convocacoes',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'recursosHumanos',
      },
      ...mapFolhaPontoToV2(authData, moduleConfigurations, 'recursosHumanos'),
      {
        title: 'Controle de Jornada',
        path: '/v2/controle-frequencia',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'recursosHumanos',
      },
      {
        title: 'Holerites',
        path: '/v2/olerites',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'recursosHumanos',
      },
      {
        title: 'Banco de horas',
        path: '/v2/controle-ponto',
        show: process.env.REACT_APP_CONTROLE_PONTO_VIEW.split(',').includes(authData.email),
        key: 'recursosHumanos',
      },
    ],
  },
  {
    title: 'Gestão Veicular',
    icon: <DirectionsCarIcon sx={iconSx} />,
    show: gestaoVeicularSectionShow({ authData, userRole, variant: 'v2' }),
    key: 'gestaoVeicular',
    items: renderGestaoVeicular({
      authData,
      userRole,
      moduleConfigurations,
      variant: 'v2',
    }),
  },
  {
    title: 'Financeiro',
    icon: <AttachMoneyIcon sx={iconSx} />,
    show: !isUsuarioUsoMuto(authData.email),
    key: 'financeiro',
    items: [
      {
        title: 'Aportes',
        path: '/v2/aportes',
        isActivePath: (pathname) => pathname.includes('/aporte'),
        show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Cartões',
        path: '/v2/cartoes',
        show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Notas',
        path: '/v2/notas',
        show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Relatório Financeiro',
        path: '/v2/relatorio-financeiro',
        show: canViewFinanceiroReport({
          authData,
          userRole,
          moduleConfigurations,
        }),
        key: 'financeiro',
      },
    ],
  },
  {
    title: 'Almoxarifado',
    icon: <PostAddIcon sx={iconSx} />,
    show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
    key: 'almoxarifado',
    items: [
      {
        title: 'Estoque',
        path: '/v2/estoques',
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'almoxarifado',
      },
      {
        title: 'Pedidos',
        path: '/v2/estoque/pedidos',
        isActivePath: (pathname) => pathname.includes('/v2/estoque/pedidos'),
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'almoxarifado',
      },
      {
        title: 'Produtos',
        path: '/v2/estoque/produtos',
        isActivePath: (pathname) => pathname.includes('/v2/estoque/produtos'),
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'almoxarifado',
      },
    ],
  },
  {
    title: 'Equipamentos',
    icon: <BuildIcon sx={iconSx} />,
    path: '/v2/equipamentos',
    show:
      process.env.REACT_APP_EQUIPAMENTO_VIEW.split(',').includes(authData.email)
      && !isUsuarioUsoMuto(authData.email),
  },
  {
    title: 'Usuários',
    icon: <PeopleAltIcon sx={iconSx} />,
    show: !isUsuarioUsoMuto(authData.email),
    key: 'usuarios',
    items: [
      {
        title: 'Listagem',
        path: '/usuarios',
        show: true,
        key: 'usuarios',
      },
      {
        title: 'Documentos e Certificados',
        path: '/usuarios-documentos',
        show:
          authData.email === 'vinicius_admin' ||
          authData.email === 'MARIA' ||
          authData.email === 'REGIANE MARIA DE OLIVEIRA' ||
          authData.email === 'JORDANIA FLORENCIO MOTA',
        key: 'usuarios',
      },
    ],
  },
  // {
  //   title: 'Controle de Caixa',
  //   icon: <AttachMoneyIcon sx={iconSx} />,
  //   path: '/v2/controle-caixa',
  //   show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
  // },
  {
    title: 'Procedimentos e Normas',
    icon: <ListAltIcon sx={iconSx} />,
    path: '/v2/procedimentos-normas',
    show: !isUsuarioUsoMuto(authData.email),
  },
  {
    title: 'Configurações',
    icon: <SettingsIcon sx={iconSx} />,
    show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
    key: 'configurations',
    items: [
      {
        title: 'Documentos',
        path: '/v2/documentos',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Malhas',
        path: '/v2/malhas',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Gerências',
        path: '/v2/gerencias',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Polos',
        path: '/v2/polos',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Subestações',
        path: '/v2/subestacoes',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Alimentadores',
        path: '/v2/alimentadores',
        show: true,
        key: 'configurations',
      },
    ],
  },
];
